<template lang="html">
  <UHeader
    class="app-header"
    mode="drawer"
    :ui="{
      container: 'sm:p-0!',
      toggle: 'me-0!',
      left: 'min-w-0 flex-1 overflow-hidden',
      right: 'shrink-0',
    }"
  >
    <template #left>
      <UNavigationMenu
        :items="leftMenu"
        color="tertiary"
        :ui="{ link: 'text-tertiary-500' }"
        class="hidden lg:flex"
      />
      <a
        href="/"
        class="inline-flex h-14 max-w-full min-w-0 items-center lg:hidden"
      >
        <UIcon
          :name="brandIcons.logoLargeDark"
          class="h-auto max-h-full w-auto max-w-full text-[length:3.5rem] leading-none"
        />
      </a>
    </template>

    <template #default>
      <a href="/" class="inline-flex h-14 shrink-0 items-center">
        <UIcon :name="brandIcons.logoLargeDark" class="h-14 w-auto" />
      </a>
    </template>

    <template #right>
      <UNavigationMenu
        :items="rightMenu"
        variant="link"
        color="tertiary"
        :ui="{ linkLeadingIcon: 'text-tertiary-500' }"
      >
        <template #user>
          <UButton
            v-if="!isFullAuth"
            icon="i-lucide-user"
            variant="link"
            color="tertiary"
            class="cursor-pointer p-0"
            @click="openAuth()"
          />
          <UDropdownMenu v-else :items="userMenu">
            <UChip
              :show="notificationsStore.hasUnread"
              color="primary"
              size="sm"
              inset
            >
              <UButton
                icon="i-lucide-user"
                variant="link"
                color="tertiary"
                class="cursor-pointer p-0"
              />
            </UChip>

            <template #notifications-trailing>
              <div
                v-if="notificationsStore.hasUnread"
                class="flex items-center gap-1.5"
              >
                <UBadge color="primary" variant="subtle" size="sm">
                  {{ notificationsStore.unreadCount }}
                </UBadge>
                <div class="h-1.5 w-1.5 rounded-full bg-primary" />
              </div>
            </template>
          </UDropdownMenu>
        </template>
      </UNavigationMenu>
    </template>

    <template #body>
      <UNavigationMenu
        :items="leftMenu"
        orientation="vertical"
        :ui="{ link: 'text-tertiary-500' }"
      />
    </template>
  </UHeader>

  <SearchModal v-model:open="isSearchOpen" />
</template>

<script lang="ts" setup>
import type { DropdownMenuItem, NavigationMenuItem } from '@nuxt/ui'

import { useCartModal } from '~/app/AltApplication/Modules/Cart/composables/useCartModal'
import { useNotifications } from '~/app/AltApplication/Modules/Notifications/composables/useNotifications'
import { useSearchModal } from '~/app/AltApplication/Modules/Products/composables/useSearchModal'
import { useReviewsEnabled } from '~/app/AltApplication/Modules/Reviews/composables/useReviewsConfig'
import { brandIcons } from '~/app/theme/brand'

const { openAuth } = useAuthInteractive()
const {
  isOpen: searchModalOpenRef,
  open: openSearch,
  close: closeSearch,
} = useSearchModal()
const { store, logout } = useAuth()
const { store: userStore } = useUser()
const { store: cartStore } = useCartModule()
const { store: notificationsStore, routing: notificationsRouting }
  = useNotifications()
const { send } = useRequest()
const { open } = useCartModal()
const reviewsEnabled = useReviewsEnabled()

const isFullAuth = computed(() => !!store.token && !store.session?.isAnon)

const isSearchOpen = computed({
  get: () => searchModalOpenRef.value,
  set: (value: boolean) => {
    if (value) {
      openSearch()
    } else {
      closeSearch()
    }
  },
})

const leftMenu = computed<NavigationMenuItem[]>(() => [
  {
    label: 'FAQ',
    to: '/faq',
  },
  {
    label: 'О бренде',
    to: '/about',
  },
  {
    label: 'Новости',
    to: '/news',
  },
  {
    label: 'Блог',
    to: '/blog',
  },
  // {
  //   label: 'Каталог',
  //   to: { name: 'ProductsIndex' },
  // },
])
const rightMenu = computed<NavigationMenuItem[]>(() => [
  {
    icon: 'i-lucide-search',
    onSelect: () => {
      openSearch()
    },
  },
  {
    to: '#',
    slot: 'user',
  },
  {
    icon: 'i-lucide-shopping-cart',
    label: cartStore.totalLength ? `[ ${cartStore.totalLength} ]` : null,
    class: 'hover:cursor-pointer',
    onSelect: () => {
      open()
    },
  },
])
const userMenu = computed<DropdownMenuItem[][]>(() => [
  [
    {
      label: userStore.user?.email || userStore.user?.phone || 'Пользователь',
      to: '/profile/edit',
      class: 'font-bold',
    },
  ],
  [
    {
      label: 'Уведомления',
      icon: 'i-lucide-bell',
      to: notificationsRouting.index,
      slot: 'notifications',
    },
    {
      label: 'Мои заказы',
      icon: 'i-lucide-package',
      to: '/profile/orders',
    },
    {
      label: 'Мои бонусы',
      icon: 'i-lucide-wallet',
      to: '/profile/loyalty',
    },
    ...(reviewsEnabled.value
      ? [{
          label: 'Мои отзывы',
          icon: 'i-lucide-message-square-quote',
          to: '/profile/reviews',
        }]
      : []),
    {
      label: 'Избранное',
      icon: 'i-lucide-heart',
      to: '/profile/favorites',
    },
  ],
  [
    {
      label: 'Выйти',
      icon: 'i-lucide-log-out',
      onSelect: () => {
        send(
          async () => {
            await logout()
            navigateTo('/')
          },
          {
            successMessage: 'Вы вышли из системы',
            confirm: {
              message: 'Вы уверены, что хотите выйти?',
              accept: 'Выйти',
              reject: 'Отмена',
            },
          },
        )
      },
    },
  ],
])
</script>
